import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface Project { id: string; code: string; name: string; status: string; }

interface Props {
  onClose: () => void;
  workerId: string;
  workerName: string;
}

export function QrSignInModal({ onClose, workerId, workerName }: Props) {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });
  const liveProjects = projects.filter((p) => p.status === 'live');

  const sign = useMutation({
    mutationFn: async (type: 'in' | 'out') => {
      let lat = 0, lng = 0, accuracy = 0;
      try {
        const pos = await new Promise<GeolocationPosition>((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 }));
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
        accuracy = pos.coords.accuracy ?? 0;
      } catch { /* geolocation optional */ }
      return api.post('/signin-records', { workerId, projectId, type, lat, lng, accuracy, device: 'qr' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signin-records'] });
      toast.success('Recorded', `${workerName} signed`);
      onClose();
    },
    onError: () => toast.error('Sign failed'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="qrsi-title">✅ Sign In / Out via QR</div>
        <div className="modal-sub" id="qrsi-sub">{workerName}</div>
        <div id="qrsi-body" style={{ padding: 12 }}>
          <div className="form-row">
            <label className="form-label">Project</label>
            <select className="form-select" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">— Select project —</option>
              {liveProjects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', padding: 10 }} onClick={() => sign.mutate('in')} disabled={sign.isPending || !projectId}>✅ Sign In</button>
            <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center', padding: 10 }} onClick={() => sign.mutate('out')} disabled={sign.isPending || !projectId}>🚪 Sign Out</button>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
